import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

/**
 * The data table — ARCHITECTURE §16.5.
 *
 * This is the screen an operator spends the day in, and the one the client
 * judged the panel by, so the specification is followed literally: 40px rows,
 * a 36px uppercase header, `--color-line` separation with **no shadow**, no
 * zebra striping, and a sticky header that gains a rule and an opaque
 * background once content scrolls under it.
 *
 * Three properties are load-bearing rather than decorative:
 *
 *   * **Identifier columns are monospace.** An operator compares an ICCID
 *     character by character against the SIM in their hand; proportional digits
 *     make that materially harder. Same reader who must spot the wrong number
 *     in a disconnect confirmation.
 *   * **Numeric columns are right-aligned with tabular figures**, so digits line
 *     up down the column and a wrong order of magnitude is visible.
 *   * **Loading is skeleton rows matching the real column widths**, not a
 *     spinner, so the layout does not jump when data lands — and the header
 *     stays, so the columns still explain what is coming.
 *
 * Row hover is a `surface-alt` fill and nothing else: no colour shift, no lift.
 * Rows must not animate on appearing or reordering — the estate polls, and a
 * list that moves under the reader is worst exactly when they are reading a
 * number off it.
 *
 * The frame is the app's brightest plane (`.card`, `bg-surface`) sitting on a
 * recessed canvas, because the redesign's one structural rule is that
 * brightness means data. It takes `--elevation-0` rather than the card's
 * default shadow: a table anchors a screen, it does not float above it. The
 * two border weights do the work instead — `--color-line-strong` frames the
 * card and rules off the header, `--color-line` divides rows — so head/body is
 * visibly the strongest boundary inside the frame.
 */

export type ColumnKind = 'text' | 'numeric' | 'mono' | 'status' | 'actions' | 'select';

export interface Column<T> {
  key: string;
  header: string;
  render(row: T): ReactNode;
  /**
   * How the column is aligned and typeset. `mono` for identifiers, `numeric`
   * for figures, `status` for a centred pill, `actions` for a right-aligned
   * fixed column, `select` for the row checkbox.
   */
  kind?: ColumnKind;
  /** @deprecated use `kind: 'numeric'`. Kept so existing callers still compile. */
  numeric?: boolean;
  /** Fixed width, e.g. `'8rem'`. Status and action columns want one. */
  width?: string;
}

/** `compact` selects `--table-row-h-compact` for a dense secondary table. */
export type TableDensity = 'default' | 'compact';

function alignmentFor(kind: ColumnKind): string {
  switch (kind) {
    case 'numeric':
      return 'text-right tabular-nums';
    case 'status':
      return 'text-left';
    case 'actions':
      return 'text-right';
    case 'mono':
      /*
        Three properties, and each is doing a job for the same reader.

        `tabular-nums` is not redundant beside a monospace family: the stack
        ends in a generic `monospace`, and on a machine where that resolves to
        a font with proportional *figures* in an otherwise fixed advance —
        which several system fallbacks do — a column of ICCIDs stops lining
        up at exactly the moment the fallback kicks in. The feature costs
        nothing when the family is already fixed-pitch.

        `--tracking-ident` opens the digits far enough to be compared one at a
        time rather than read as a bar (the token's own docblock has the
        argument).

        `whitespace-nowrap` because an identifier that wraps is an identifier
        an operator will mis-copy. The table's own horizontal scroller is the
        correct answer to a narrow viewport; a broken ICCID is not.
      */
      return 'text-left font-[family-name:var(--font-mono)] tabular-nums tracking-[var(--tracking-ident)] whitespace-nowrap';
    case 'select':
      // The checkbox is centred in its own narrow gutter rather than left-set
      // against the frame: a 16px control on a 20px indent reads as a stray
      // mark, and the selected row's 2px indicator lands on top of it.
      return 'text-center';
    default:
      return 'text-left';
  }
}

function kindOf<T>(col: Column<T>): ColumnKind {
  return col.kind ?? (col.numeric ? 'numeric' : 'text');
}

/**
 * The outer columns get a wider gutter than the interior ones so the data sits
 * inside the frame rather than against it. Interior padding stays uniform, or
 * the columns stop reading as a grid.
 *
 * `select` is the one exception, and it is a density decision rather than a
 * cosmetic one. A leading checkbox column carrying the full 20px outer gutter
 * plus 16px of interior padding spends 56px of an estate table's width on a
 * 16px control, and every identifier column — the ones the screen exists for —
 * starts that much further from the eye. It gets a gutter sized to the control
 * instead, and the columns after it recover the space.
 */
function paddingFor(index: number, count: number, kind: ColumnKind): string {
  if (kind === 'select') return 'pl-3 pr-2';
  if (count === 1) return 'px-5';
  if (index === 0) return 'pl-5 pr-4';
  if (index === count - 1) return 'pl-4 pr-5';
  return 'px-4';
}

/**
 * A skeleton cell the width the real column will be.
 *
 * The docblock above promises the loading state does not move the layout when
 * data lands, and a uniform 12rem bar in every column is not that promise kept
 * — it is one bar repeated, which then jumps to five different widths. These
 * are the rendered widths of the real content: a checkbox, a pill, a figure,
 * an identifier, a sentence.
 */
function skeletonWidthFor(kind: ColumnKind): string {
  switch (kind) {
    case 'select':
      return 'w-4';
    case 'status':
      return 'w-[5.5rem]';
    case 'numeric':
      return 'ml-auto w-10';
    case 'actions':
      return 'ml-auto w-14';
    case 'mono':
      return 'w-[9rem]';
    default:
      return 'w-full max-w-[10rem]';
  }
}

export function Table<T>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  loading = false,
  skeletonRows = 6,
  isSelected,
  density = 'default',
}: {
  caption: string;
  columns: Column<T>[];
  rows: readonly T[];
  rowKey(row: T): string;
  empty?: ReactNode;
  loading?: boolean;
  skeletonRows?: number;
  /** Selected rows take a 2px `primary` left border — never a primary tint. */
  isSelected?(row: T): boolean;
  /** Defaults to the §16.5 40px row. `compact` is for a table inside a panel. */
  density?: TableDensity;
}): ReactElement {
  const rowHeight =
    density === 'compact' ? 'h-[var(--table-row-h-compact)]' : 'h-[var(--table-row-h)]';

  /**
   * A horizontal scroll with no edge is a scroll a reader does not know exists:
   * the last column simply looks like the last column. The fade is rendered
   * only while the table genuinely overflows, because a permanent gradient over
   * a table that fits is decoration lying about an affordance.
   *
   * It is measured rather than assumed — column count tells you nothing about
   * rendered width — and it hangs off the **frame**, not the scroller. An
   * `::after` inside a scroll container is positioned against the scrollable
   * area and travels with the content, so it would appear at the far right of
   * the widest row instead of at the right edge of the viewport.
   */
  const scroller = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;

    const measure = (): void => {
      // 1px of slack: sub-pixel layout rounding otherwise reports a permanent
      // overflow on a table that fits exactly.
      setOverflowing(el.scrollWidth - el.clientWidth > 1);
    };
    measure();

    // Absent in jsdom, where there is no layout to observe in the first place.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    const table = el.firstElementChild;
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [columns.length, rows.length, loading]);

  const header = (
    <thead
      // `top-0`, not `top-[var(--topbar-h)]` — this thead's sticky containing
      // block is the card's own `overflow-x-auto` scroller, not the page. The
      // topbar offset floated the header 52px down INSIDE the card, where it
      // sat permanently over the first row: occluding it and swallowing every
      // click meant for its links and checkboxes. Within the scroller the
      // context bar is not overhead, so there is nothing to offset past.
      className="sticky top-0 z-10 bg-surface shadow-[var(--elevation-1)]"
    >
      <tr>
        {columns.map((col, index) => {
          const kind = kindOf(col);
          return (
            <th
              key={col.key}
              scope="col"
              style={col.width ? { width: col.width } : undefined}
              // `whitespace-nowrap`: a two-word header that wraps grows the
              // header band past its 36px token and pushes every row down with
              // it. `select-none` so dragging across the head to compare two
              // columns does not leave the labels highlighted.
              className={`h-[var(--table-header-h)] select-none whitespace-nowrap border-b border-line-strong bg-surface text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted ${paddingFor(
                index,
                columns.length,
                kind,
              )} ${alignmentFor(kind)}`}
            >
              {/* A `select` column's header is for the screen reader only: the
                  word "Select" over a 16px checkbox is wider than the control
                  it labels and reads as a column of its own. */}
              {kind === 'select' ? <span className="sr-only">{col.header}</span> : col.header}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  return (
    // Borders, not shadow: the frame does not float (§16.2 elevation).
    //
    // `overflow-hidden` on the FRAME, and no radius on the scroller. The
    // scroller used to carry `rounded-[var(--radius-md)]` while `.card` rounds
    // to `--radius-lg`, so at any tenant radius above zero the rows clipped to
    // a tighter curve than the frame drew and left a sliver of border showing
    // through at all four corners. One element clips, and it is the one that
    // owns the corner.
    <div
      className={`card relative overflow-hidden shadow-[var(--elevation-0)] ${
        overflowing ? 'scroll-fade-x' : ''
      }`}
    >
      <div ref={scroller} className="overflow-x-auto">
        <table className="w-full border-collapse text-[length:var(--text-sm)]">
          <caption className="sr-only">{caption}</caption>
          {header}
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={`skeleton-${i}`} className="border-t border-line">
                    {columns.map((col, index) => (
                      <td
                        key={col.key}
                        className={`${rowHeight} ${paddingFor(index, columns.length, kindOf(col))}`}
                      >
                        {/* Matches the real column, so nothing jumps when data lands. */}
                        <div
                          aria-hidden="true"
                          className={`shimmer h-3 rounded-[var(--radius-sm)] ${skeletonWidthFor(
                            kindOf(col),
                          )}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    // `focus-within` is not decoration: several columns render a
                    // link, and tabbing through the estate otherwise moves a 2px
                    // ring around a page with no indication of which of a
                    // thousand rows it belongs to.
                    className={`border-t border-line transition-colors duration-[var(--motion-fast)] hover:bg-surface-alt focus-within:bg-surface-alt focus-within:ring-1 focus-within:ring-inset focus-within:ring-line-strong ${
                      isSelected?.(row)
                        ? 'border-l-2 border-l-primary bg-surface-alt'
                        : 'border-l-2 border-l-transparent'
                    }`}
                  >
                    {columns.map((col, index) => (
                      <td
                        key={col.key}
                        className={`${rowHeight} align-middle text-body ${paddingFor(
                          index,
                          columns.length,
                          kindOf(col),
                        )} ${alignmentFor(kindOf(col))}`}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* The header is kept above: the columns still explain what would be here. */}
      {/* No rule above it: the header's own `line-strong` bottom border is
          already directly overhead, and two hairlines 1px apart read as a
          rendering fault rather than as structure. */}
      {!loading && rows.length === 0 && empty ? (
        <div className="p-[var(--space-6)]">{empty}</div>
      ) : null}
    </div>
  );
}
